import { type Database } from "@fitness-app/supabase";

import { type AutomationSettings } from "./Automation";
import { type RadixColor } from "./CheckoutVariant";
import { type OrderListItem } from "./Order";
import { type CrossSell, type CrossSellProductInfo, type Product } from "./Product";

export type SalesFunnelEntity = Database["public"]["Tables"]["sales_funnel"]["Row"];
export type SalesFunnelStatsEntity = Database["public"]["Tables"]["sales_funnel_stats"]["Row"];

export enum CheckoutVariantStatus {
  Active = "active",
  Inactive = "inactive",
  Archived = "archived",
}

export enum CheckoutVariantTarget {
  All = "all",
  New = "new",
  Renewal = "renewal",
}

interface PricingFeature {
  name: string;
  value?: string;
  originalPrice?: string;
  isFree?: boolean;
  hidden?: boolean;
}

interface PricingPlan {
  title: string;
  best?: boolean;
  subtitle?: string;
  regularPrice: string;
  promoPrice: string;
  monthlyInfo: string;
  oneTimeInfo?: string;
  features: PricingFeature[];
  bonuses: PricingFeature[];
}

export interface CustomVariantConsent {
  label: string;
  url: string | null;
  required: boolean;
  name: string;
  applyToProducts: true;
  filteredByPriceId?: null | string[];
}

export interface PageConfiguration {
  pricing?: PricingPlan[];
  productNameOverride?: string;
  productImageOverride?: string;
  productDescriptionOverride?: string;
  customConsents?: CustomVariantConsent[];
  pricingDisabled?: boolean;
  timer?:
    | false
    | {
        title: string;
        description: string;
        startInfo: string;
        titleColor?: RadixColor;
      };
  promoVideo?: {
    title: string;
    videoId: string;
    description?: string;
    hideIcons?: boolean;
  };
  enableLandingPage?: boolean;
  videoOnTop?: boolean;
  renewalFallback?: string;
  previousStep?: {
    price: number;
  };
}

export enum FunnelStepType {
  Landing = "landing",
  Checkout = "checkout",
  Upsell = "upsell",
  Downsell = "downsell",
  ThankYou = "thank_you",
}

export interface GenericSalesFunnelStep<Type extends FunnelStepType, Config> {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  description?: string;
  path: string;
  type: Type;
  stepConfig: Config & {
    /**
     * Which page renders this step on the dynamic `/oferta/<slug>/<path>` route. Funnels with
     * their own route folder ignore it. Missing = the first template of the step type.
     */
    pageTemplate?: FunnelPageTemplate | null;
    /**
     * A page from the landing builder (`landing_page.id`, its `kind` matching the step type)
     * rendering this step instead of `pageTemplate` (TRE-2356).
     */
    landingPageId?: string | null;
  };
}

/**
 * How the landing shows free seats in a weekly group (Reset). `real` counts paid orders only;
 * `simulated` walks the number down through the enrolment week from `start` to `floor`, and the
 * real count still wins whenever it is lower.
 */
export interface CohortSeatsConfig {
  mode: "real" | "simulated";
  /** Seats in a group. */
  limit: number;
  /** Simulated seats shown on Monday 00:00. */
  start?: number;
  /** Simulated seats reached on Sunday 23:59; at least 1, so the simulation never fills a group. */
  floor?: number;
}

/**
 * The challenge reward the Bikini landing names in zł ("441 zł na Program Hollywood"):
 * `default` keeps the landing's own amount, `custom` shows `amount`, `hidden` names no amount.
 */
export interface FunnelRewardConfig {
  mode: "default" | "custom" | "hidden";
  /** Whole zł, used with `mode: "custom"`. */
  amount?: number | null;
}

const toWholeNumber = (value: unknown, fallback: number) =>
  typeof value === "number" && Number.isFinite(value) ? Math.round(value) : fallback;

/**
 * Whole numbers, `1 <= floor <= start <= limit`. Used when saving in the admin and when reading
 * the stored JSON, so a lowered limit or a decimal never reaches the landing.
 */
export const normalizeCohortSeatsConfig = (config: Partial<CohortSeatsConfig>): CohortSeatsConfig => {
  const limit = Math.max(1, toWholeNumber(config.limit, 30));
  const start = Math.min(Math.max(1, toWholeNumber(config.start, limit)), limit);
  const floor = Math.min(Math.max(1, toWholeNumber(config.floor, 1)), start);
  return { mode: config.mode === "simulated" ? "simulated" : "real", limit, start, floor };
};

/**
 * Funnels with their own route folder under `apps/ecommerce/src/pages/oferta/`: their pages come
 * from code, so a step's `pageTemplate` / `landingPageId` has no effect there. Kept in sync with
 * the folders by a test in ecommerce.
 */
export const FUNNELS_WITH_OWN_ROUTES = [
  "6tygodnidobikini",
  "6tygodnidobikini-2",
  "6tygodnidobikini-new",
  "6tygodnidobikini-promo",
  "6tygodnidobikini-promocja",
  "6tygodnidobikini-promocja-sub",
  "6tygodnidobikini-vip",
  "hollywood-bikini-continue",
  "hollywood-bikini-continue-2",
  "hollywood-reset",
  "hollywood-reset-promo",
] as const;

export const hasOwnFunnelRoutes = (slug: string | null | undefined) =>
  Boolean(slug) && (FUNNELS_WITH_OWN_ROUTES as readonly string[]).includes(slug!);

/**
 * Page templates a funnel step can be rendered with on the dynamic `/oferta/<slug>/<path>`
 * route, per step type. The first entry of each list is the default.
 */
export const FUNNEL_PAGE_TEMPLATES = {
  [FunnelStepType.Landing]: [
    { value: "bikini", label: "6 Tygodni do Bikini" },
    { value: "bikiniNewMetamorphoses", label: "6 Tygodni do Bikini - nowe metamorfozy" },
    { value: "bikini2026", label: "6 Tygodni do Bikini 2026" },
    { value: "hollywoodResetQuiz", label: "Hollywood Reset: quiz" },
  ],
  [FunnelStepType.Checkout]: [
    { value: "bikini", label: "6 Tygodni do Bikini" },
    { value: "bikini2026", label: "6 Tygodni do Bikini 2026" },
    { value: "hollywoodReset", label: "Hollywood Reset" },
  ],
  [FunnelStepType.Upsell]: [
    { value: "bikiniRecipes", label: "Bikini: 100+ przepisów premium" },
    { value: "bikiniCompleteOrder", label: "Bikini: kompletne zamówienie" },
  ],
  [FunnelStepType.Downsell]: [{ value: "bikiniRecipesDownsell", label: "Bikini: przepisy za pół ceny" }],
  [FunnelStepType.ThankYou]: [
    { value: "bikini", label: "6 Tygodni do Bikini" },
    { value: "hollywoodReset", label: "Hollywood Reset" },
  ],
} as const satisfies Record<FunnelStepType, readonly { value: string; label: string }[]>;

export type FunnelPageTemplate = (typeof FUNNEL_PAGE_TEMPLATES)[FunnelStepType][number]["value"];

export const getFunnelStepPageTemplate = (step: Pick<SalesFunnelStep, "type" | "stepConfig">): FunnelPageTemplate => {
  const templates: readonly { value: FunnelPageTemplate }[] = FUNNEL_PAGE_TEMPLATES[step.type];
  const selected = step.stepConfig.pageTemplate;

  return templates.find((template) => template.value === selected)?.value ?? templates[0]!.value;
};

export type LandingStep = GenericSalesFunnelStep<
  FunnelStepType.Landing,
  {
    pageConfiguration: PageConfiguration;
    productId: string;
    cohortSeats?: CohortSeatsConfig | null;
    reward?: FunnelRewardConfig | null;
    /**
     * Whole zł the landing strikes through before the checkout price ("z 597 zł na 197 zł").
     * Without it the landing strikes through only a promo price of the checkout.
     */
    strikethroughPrice?: number | null;
  }
>;

/** The phone field on a funnel's checkout, in place of the cart settings' one. */
export type CheckoutStepPhoneField = "required" | "optional" | "hidden";

export type CheckoutStep = GenericSalesFunnelStep<
  FunnelStepType.Checkout,
  {
    prices: string[];
    productId: string;
    pageConfiguration: PageConfiguration | null;
    /** Overrides the cart settings (product, funnel); unset = as they say. */
    phoneField?: CheckoutStepPhoneField | null;
    /**
     * The order bumps this checkout offers in place of the product's (`Product.crossSell`), which
     * every checkout of the product shares. Unset = the product's; `products: []` = none.
     */
    crossSell?: Product["crossSell"] | null;
  }
>;

/**
 * The checkout step of a funnel that sells `productId` (`stepConfig.productId`, else the funnel's
 * product): the step whose settings shape that funnel's checkout page.
 */
export const findFunnelCheckoutStep = (
  funnel: Pick<SalesFunnel, "productId" | "steps">,
  productId: string,
): CheckoutStep | null =>
  funnel.steps.find(
    (step): step is CheckoutStep =>
      step.type === FunnelStepType.Checkout && (step.stepConfig.productId || funnel.productId) === productId,
  ) ?? null;

/**
 * The order bumps a checkout offers: the funnel's checkout step's own, else the product's. The
 * checkout page lists these and create-order accepts only these.
 */
export const checkoutCrossSell = (
  product: Pick<Product, "crossSell">,
  checkoutStep: Pick<CheckoutStep, "stepConfig"> | null | undefined,
): CrossSell[] => (checkoutStep?.stepConfig.crossSell ?? product.crossSell)?.products ?? [];

export type UpsellStep = GenericSalesFunnelStep<
  FunnelStepType.Upsell,
  {
    priceId: string;
    productId: string;
    pageConfiguration: PageConfiguration | null;
    customConsents?: CustomVariantConsent[];
    offerAcceptedNextStepId: string;
    offerRejectedNextStepId: string;
  }
>;

export type DownsellStep = GenericSalesFunnelStep<
  FunnelStepType.Downsell,
  {
    priceId: string;
    productId: string;
    pageConfiguration: PageConfiguration | null;
    customConsents?: CustomVariantConsent[];
    offerAcceptedNextStepId: string;
    offerRejectedNextStepId: string;
  }
>;

export type ThankYouStep = GenericSalesFunnelStep<
  FunnelStepType.ThankYou,
  {
    pageConfiguration: PageConfiguration | null;
    productId: string;
  }
>;

export type SalesFunnelStep = LandingStep | CheckoutStep | UpsellStep | DownsellStep | ThankYouStep;

export interface SalesFunnel extends Omit<SalesFunnelEntity, "metadata" | "steps" | "configuration" | "cartSettings"> {
  steps: SalesFunnelStep[];
  metadata:
    | (Record<string, string | number | null | boolean | string[] | number[]> & {
        allowManualPaymentForPrices?: (string | "all")[];
      })
    | null;
  status: CheckoutVariantStatus;
  target: CheckoutVariantTarget;
  pageConfiguration: PageConfiguration | null;
  /**
   * Funnel-level override of the product's `AutomationSettings`. When present, these values
   * take precedence over the product's automation settings (but are overridden by a variant's
   * `cartSettings` if one is set). See `ProductCheckoutClientService.composeProductInfo`.
   */
  cartSettings?: Partial<AutomationSettings> | null;
}

export interface BaseSalesFunnelStats extends Omit<SalesFunnelStatsEntity, "metadata" | "eventType" | "data"> {
  eventType: "purchased" | "viewed" | "rejected" | "conversion";
  eventName: string;
  data: Record<string, unknown>;
  metadata?: Record<string, unknown> | null;
}

export type GenericSalesFunnelStepEvent<
  EventName extends string,
  EventType extends "viewed" | "rejected" | "purchased" | "conversion",
  EventData extends Record<string, unknown>,
> = BaseSalesFunnelStats & {
  eventName: EventName;
  eventType: EventType;
  data: EventData;
};

export type ViewEventName = "checkoutViewed" | "upsellViewed" | "downsellViewed" | "thankYouViewed" | "landingViewed";

export type ViewSalesFunnelStep = GenericSalesFunnelStepEvent<
  ViewEventName,
  "viewed",
  { path: string; stepId: string; productId: string | null; stepType: FunnelStepType }
>;
export type RejectedEventName =
  | "checkoutRejected"
  | "upsellRejected"
  | "downsellRejected"
  | "thankYouRejected"
  | "landingRejected";
export type RejectedSalesFunnelStep = GenericSalesFunnelStepEvent<
  RejectedEventName,
  "rejected",
  { path: string; stepId: string; productId: string; stepType: FunnelStepType; stepSlug: string; funnelSlug: string }
>;
export type PurchaseEventName = "checkoutPurchased" | "upsellPurchased" | "downsellPurchased";
export type PurchaseSalesFunnelStep = GenericSalesFunnelStepEvent<
  PurchaseEventName,
  "purchased",
  {
    amount: number;
    currency: string;
    path: string;
    stepId: string;
    productId: string;
    stepType: FunnelStepType;
    crossSellProducts?: CrossSellProductInfo[];
    items?: OrderListItem[];
  }
>;

export type ConversionEventName = "landingConversion";

export type ConversionSalesFunnelStep = GenericSalesFunnelStepEvent<
  ConversionEventName,
  "conversion",
  {
    action: "cta_click" | "cta_view" | "cta_submit";
    path: string;
    stepId: string;
    productId: string;
    stepType: FunnelStepType;
  }
>;

export type SalesFunnelStats =
  | ViewSalesFunnelStep
  | RejectedSalesFunnelStep
  | PurchaseSalesFunnelStep
  | ConversionSalesFunnelStep;
